/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Dropdown,
  Input,
  Menu,
  message,
  notification,
  Popconfirm,
  Popover,
  Row,
  Select,
  Space,
  Table,
  Tag,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  EyeOutlined,
  FileExcelOutlined,
  FilterOutlined,
  SearchOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import EllipsisDropdown from "components/shared-components/EllipsisDropdown";
import Flex from "components/shared-components/Flex";
import utils from "utils";
import { useTranslation } from "react-i18next";
import { useExact } from "../../../../../data/hooks/exact/useExact";
import styled from "styled-components";
import { FIRESTORE_CUSTOMERS_TABLE, FIRESTORE_USERS_TABLE } from "../../../../../constants/FirestoreConstant";
import { deleteDoc, doc, updateDoc } from "firebase/firestore";
import { firestore } from "../../../../../configs/FirebaseConfig";
import { getUserDataById } from "../../../../../functions/users";
import dayjs from "dayjs";
import { formatPrice, getUserStatus } from "../../../../../functions/formatters";
import { CURRENCY, DEFAULT_DELIVERY_COST, FREE_DELIVERY_FROM_PRICE } from "../../../../../constants/DeliveryConstants";
import { isMobile } from "../../../../../functions/browser";
import { USER_STATUS_ACTIVE, USER_STATUS_NEW, USER_STATUS_REJECTED } from "../../../../../constants/UserConstant";
import { CHEFS_FARM, COMPANY_CONSTANTS, GROWY } from "../../../../../constants/CompanyConstants";
import { DATE_FORMAT_DD_MM_YYYY } from "../../../../../constants/DateConstant";
import { getCustomerExcelExportData } from "../../../../../functions/excel";
import EditCompanyDetailsModal from "../modals/EditCompanyDetailsModal";
import EditPaymentDetailsModal from "../modals/EditPaymentDetailsModal";
import EditDeliveryDetailsModal from "../modals/EditDeliveryDetailsModal";
import QuickEditModal from "../modals/QuickEditModal";
import AddCompanyModal from "../modals/AddCompanyModal";
import { MARKETING_CODES, PRODUCT_COMPANY_GROUPS } from "../../../../../constants/ProductConstant";
import ExcelExport from "../../../../../components/shared-components/ExcelExport";
import { useCustomers } from "../../../../../data/hooks/firestore/useCustomers";
import { useCmsUsers } from "../../../../../data/hooks/firestore/useCmsUsers";
import { useWebshopUsers } from "../../../../../data/hooks/firestore/useWebshopUsers";
import { useAuth } from "../../../../../data/hooks/firebase/useAuth";
import { usePriceLists } from "../../../../../data/hooks/firestore/usePriceLists";
import { useCountries } from "../../../../../data/hooks/firestore/useCountries";
import roles from "../../../../../roles";
import { createExactAccount } from "../../../../../functions/api/exact";
import { getFirebaseIDToken } from "../../../../../functions/firebase/firebase";
import { StyledSelect } from "../../../../../styles/sharedStyles";
import EditCommunicationDetailsModal from "../modals/EditCommunicationDetailsModal";
import {
  COMMUNICATION_TYPES,
  INVOICES,
  ORDER_CONFIRMATIONS,
  ORDER_UPDATES,
} from "../../../../../constants/CommunicationConstants";
import { getContactDisplay } from "../../../../../constants/ContactConstants";
import useIsMobile from "../../../../../hooks/useIsMobile";
import { NETHERLANDS_COUNTRY_CODE } from "../../../../../constants/PricingConstants";

const { Option } = Select;

const CompanyList = () => {
  const { profile, email } = useAuth();
  const { customers: companies, isLoading: companiesLoading } = useCustomers();
  const { getPriceListById, priceLists } = usePriceLists();
  const { webshopUsers, isLoading: webshopUsersLoading, getUserById } = useWebshopUsers();
  const { cmsUsers, isLoading: cmsUsersLoading } = useCmsUsers();
  const [drawerVisible, setDrawerVisible] = useState(null);
  const [addCompanyModalVisible, setAddCompanyModalVisible] = useState(false);
  const [quickEditModalVisible, setQuickEditModalVisible] = useState(false);
  const [editCompanyDetailsModalVisible, setEditCompanyDetailsModalVisible] = useState(false);
  const [editPaymentDetailsModalVisible, setEditPaymentDetailsModalVisible] = useState(false);
  const [editDeliveryDetailsModalVisible, setEditDeliveryDetailsModalVisible] = useState(false);
  const [editCommunicationDetailsModalVisible, setEditCommunicationDetailsModalVisible] = useState(false);
  const [filteredCompanies, setFilteredCompanies] = useState(companies);
  const [companyId, setCompanyId] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [clickedCompanyFilter, setClickedCompanyFilter] = useState(localStorage.getItem("companyFilterValue") || "All");
  const [marketingCodeFilter, setMarketingCodeFilter] = useState(
    localStorage.getItem("marketingCodeFilterValue") || "All",
  );
  const [countryFilter, setCountryFilter] = useState(localStorage.getItem("countryFilterValue") || "All");
  const { t } = useTranslation();
  const [filterPopoverVisible, setFilterPopoverVisible] = useState(false);
  const isMobileView = useIsMobile();
  const { getCountryDelivery, countries } = useCountries();
  const { customers: exactCustomers } = useExact();
  const company = useMemo(
    () => (companyId ? companies.find((company) => company.customerId === companyId) : null),
    [companyId, companies],
  );
  const defaultDeliverySettings = getCountryDelivery(company?.country || NETHERLANDS_COUNTRY_CODE);
  const [exactSyncLoadingCustomers, setExactSyncLoadingCustomers] = useState(new Set());
  const companyUsers = useMemo(() => {
    if (!company?.customerUsers) return null;

    return company.customerUsers.reduce((acc, user) => {
      const userData = getUserDataById(webshopUsers, cmsUsers, user);
      return userData ? [...acc, userData] : acc;
    }, []);
  }, [company]);

  // Helper function to display communication preferences
  const displayCommunicationPreferences = (communicationType) => {
    if (!company.communicationPreferences?.[communicationType.value]) {
      // Default fallbacks when no preferences are set
      const invoiceEmail = communicationType.value === INVOICES && !!company.invoiceEmail ? company.invoiceEmail : null;
      const orderer = [ORDER_CONFIRMATIONS, ORDER_UPDATES].includes(communicationType.value) ? t("orderer") : null;
      const firstActiveUser = companyUsers?.find((user) => user.isActive)?.userEmail;

      return (
        <span style={{ fontStyle: "italic", opacity: 0.5 }}>{invoiceEmail || orderer || firstActiveUser || "-"}</span>
      );
    }

    const contacts = company.communicationPreferences[communicationType.value];
    if (!contacts || contacts.length === 0) {
      return <span style={{ fontStyle: "italic", opacity: 0.5 }}>-</span>;
    }

    const displayValues = contacts.map((contact, index) => (
      <span key={index}>{getContactDisplay(contact, companyUsers)}</span>
    ));

    return (
      <>
        {displayValues.map((item, index) => (
          <span key={index}>
            {item}
            {index < displayValues.length - 1 && ", "}
          </span>
        ))}
      </>
    );
  };

  useEffect(() => {
    if (!!companies) {
      applyFilters(searchValue, clickedCompanyFilter, marketingCodeFilter, countryFilter);
    }
  }, [companies]);

  const showDrawer = (company) => {
    setDrawerVisible(true);
    setCompanyId(company.id);
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    setTimeout(() => {
      setCompanyId(null);
    }, 400);
  };

  const onPressQuickEdit = (company) => {
    setQuickEditModalVisible(true);
    setCompanyId(company.id);
  };

  const closeQuickEditModal = () => {
    setQuickEditModalVisible(false);
    setCompanyId(null);
  };

  const onPressAddButton = () => {
    setAddCompanyModalVisible(true);
  };

  const dropdownMenu = (row) => {
    return (
      <Menu>
        <Menu.Item onClick={() => showDrawer(row)}>
          <Flex alignItems="center">
            <EyeOutlined />
            <span style={{ marginLeft: 4 }}>{t("details")}</span>
          </Flex>
        </Menu.Item>
        <Menu.Item onClick={() => onPressQuickEdit(row)}>
          <Flex alignItems="center">
            <EditOutlined />
            <span style={{ marginLeft: 4 }}>{t("quick_edit")}</span>
          </Flex>
        </Menu.Item>
        <Menu.Item>
          <Popconfirm
            placement="bottom"
            title={t("confirm_delete_customer", { name: row.customerName })}
            onConfirm={() => deleteRow(row)}
            okText={t("yes")}
            cancelText={t("no")}
          >
            <a onClick={(e) => e.preventDefault}>
              <DeleteOutlined />
              {t("delete")}
            </a>
          </Popconfirm>
        </Menu.Item>
      </Menu>
    );
  };

  const exportMenu = (
    <Menu>
      <Menu.Item key={"1"}>
        <ExcelExport
          data={getCustomerExcelExportData(companies, { getPriceListById })}
          fileName={COMPANY_CONSTANTS.WEBSITE_NAME + " klanten export " + dayjs().format(DATE_FORMAT_DD_MM_YYYY)}
        >
          {t("customers_all")}
        </ExcelExport>
      </Menu.Item>
    </Menu>
  );

  const deleteRow = async (row) => {
    const users = row.customerUsers;
    try {
      await deleteDoc(doc(firestore, FIRESTORE_CUSTOMERS_TABLE, row.id));
      if (!!users && users.length > 0) {
        for (const user of users) {
          try {
            await updateDoc(doc(firestore, FIRESTORE_USERS_TABLE, user), {
              userBanned: true,
              userStatus: USER_STATUS_REJECTED,
              updatedAt: dayjs().unix(),
              updatedBy: email,
            });
          } catch (error) {
            console.error("Error updating user:", error);
          }
        }
      }
      message.success(t("notifications.client_deleted"), 4);
    } catch (error) {
      console.error("Error deleting customer:", error);
      message.error(error.message, 4);
    }
  };

  const getCustomerStatusColor = (status) => {
    if (status === USER_STATUS_ACTIVE) return "green";
    else if (status === USER_STATUS_NEW) return "orange";
    else if (status === USER_STATUS_REJECTED) return "red";
    else return "green";
  };

  const retryExactSync = async (company) => {
    setExactSyncLoadingCustomers((prev) => new Set(prev).add(company.id));

    try {
      let contactFirstName = null;
      let contactLastName = null;

      // First try to get user data
      if (company.customerUsers && company.customerUsers.length > 0) {
        const userId = company.customerUsers[0];
        const user = getUserById(userId);

        if (user) {
          contactFirstName = user.userFirstName;
          contactLastName = user.userLastName;
        }
      }

      // Fallback to company.contact if no user found
      if (!contactFirstName || !contactLastName) {
        if (company.contact?.firstName && company.contact?.lastName) {
          contactFirstName = company.contact.firstName;
          contactLastName = company.contact.lastName;
        } else {
          // No valid contact info at all
          notification.error({
            message: t("error"),
            description: t("notifications.no_contact_info_available"),
            duration: 0,
          });
          return;
        }
      }

      const token = await getFirebaseIDToken();
      const objectForExact = {
        ...company,
        id: company.customerId,
        customerId: company.customerId,
        contact: {
          userFirstName: contactFirstName,
          userLastName: contactLastName,
        },
      };

      await createExactAccount(objectForExact, token);
      message.success(t("notifications.exact_sync_success"));
    } catch (err) {
      console.error(err);
      const errorMessage =
        typeof err === "string"
          ? err
          : err.response?.data?.message || err.message || t("notifications.save_to_exact_failed");

      notification.error({
        message: t("error"),
        description: `${t("notifications.save_to_exact_failed")}: ${errorMessage}`,
        duration: 0,
      });
    } finally {
      setExactSyncLoadingCustomers((prev) => {
        const next = new Set(prev);
        next.delete(company.id);
        return next;
      });
    }
  };

  const tableColumns = [
    {
      title: t("name"),
      dataIndex: "customerName",
      sorter: (a, b) => utils.antdTableSorter(a, b, "customerName"),
      sortDirections: ["descend", "ascend"],
      defaultSortOrder: "descend",
      render: (_, data) => (
        <button className={"text-button"} onClick={() => showDrawer(data)}>
          {data.customerName}
        </button>
      ),
    },
    {
      title: t("country"),
      dataIndex: "country",
      sorter: (a, b) => utils.antdTableSorter(a, b, "country"),
    },
    // {
    //   title: t("order.orders"),
    //   dataIndex: "customerId",
    //   render: (data) => {
    //     const orderAmount = getOrderAmountForCompany(orders, data);
    //     return <span>{orderAmount}</span>;
    //   },
    //   sorter: (a, b) => {
    //     return getOrderAmountForCompany(orders, a.customerId) - getOrderAmountForCompany(orders, b.customerId);
    //   },
    // },
    {
      title: t("users"),
      dataIndex: "customerUsers",
      render: (data) => <span>{!!data ? data.length : 0}</span>,
    },
    {
      title: t("customer.delivery_address"),
      dataIndex: "customerAddress",
      render: (data) => <span>{data}</span>,
    },
    {
      title: t("delivery_days"),
      dataIndex: "customerDeliveryDays",
      render: (_, data) => (
        <span>
          {!!data.customerDeliveryDays ? (
            <CheckOutlined style={{ color: "green" }} />
          ) : (
            <CloseOutlined style={{ color: "red" }} />
          )}{" "}
          {data.deliveryLabel}
        </span>
      ),
    },
    {
      title: t("delivery_cost"),
      dataIndex: "deliveryCost",
      render: (data) => <span>{data || data === 0 ? `${CURRENCY}${formatPrice(data)}` : "-"}</span>,
    },
    {
      title: t("delivery_cost_threshold_short"),
      dataIndex: "deliveryCostThreshold",
      render: (data) => <span>{data || data === 0 ? `${CURRENCY}${formatPrice(data)}` : "-"}</span>,
    },
    {
      title: `${t("in_exact")}?`,
      render: (_, data) => (
        <span>
          {!!data.exactAccountCode ? (
            <CheckOutlined style={{ color: "green" }} />
          ) : (
            <Dropdown
              placement="bottomRight"
              overlay={
                <Menu>
                  <Menu.Item onClick={() => retryExactSync(data)}>{t("save_to_exact_database")}</Menu.Item>
                </Menu>
              }
              trigger={["hover"]}
            >
              <span
                style={{
                  color: "red",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <CloseOutlined />
                {exactSyncLoadingCustomers.has(data.id) && <SyncOutlined spin />}
              </span>
            </Dropdown>
          )}
        </span>
      ),
    },
    {
      title: "",
      render: (_, elm) => (
        <div className="text-right">
          <EllipsisDropdown menu={dropdownMenu(elm)} />
        </div>
      ),
    },
  ];

  const onSearch = (value) => {
    setSearchValue(value);
    applyFilters(value, clickedCompanyFilter, marketingCodeFilter, countryFilter);
  };

  const handleShowCompanyFilter = (value) => {
    localStorage.setItem("companyFilterValue", value);
    setClickedCompanyFilter(value);
    applyFilters(searchValue, value, marketingCodeFilter, countryFilter);
  };

  const handleMarketingCodeFilter = (value) => {
    localStorage.setItem("marketingCodeFilterValue", value);
    setMarketingCodeFilter(value);
    applyFilters(searchValue, clickedCompanyFilter, value, countryFilter);
  };

  const handleCountryFilter = (value) => {
    localStorage.setItem("countryFilterValue", value);
    setCountryFilter(value);
    applyFilters(searchValue, clickedCompanyFilter, marketingCodeFilter, value);
  };

  const applyFilters = (searchValue, companyFilterValue, marketingCodeFilterValue, countryFilterValue) => {
    let filteredData = companies;

    // Filter by company
    if (companyFilterValue !== "All") {
      filteredData = filteredData.filter((company) => company.company === companyFilterValue);
    }

    // Filter by marketing code
    if (marketingCodeFilterValue === "None") {
      filteredData = filteredData.filter((company) => !company.marketingCode);
    } else if (marketingCodeFilterValue !== "All") {
      filteredData = filteredData.filter((company) => company.marketingCode === marketingCodeFilterValue);
    }

    // Filter by country
    if (countryFilterValue !== "All") {
      filteredData = filteredData.filter((company) => company.country === countryFilterValue);
    }

    // Apply search
    if (searchValue) {
      filteredData = utils.wildCardSearchOnCustomer(filteredData, searchValue);
    }

    setFilteredCompanies(filteredData);
  };

  return (
    <>
      <Row className={"mb-2"}>
        <Col xs={24} md={16} xl={15} xxl={14}>
          <h1>{t("customers_all")}</h1>
        </Col>
        <Col xs={24}>
          <Card>
            <Space wrap style={{ width: "100%", justifyContent: "space-between", marginBottom: 6 }}>
              {!isMobileView ? (
                <Space wrap>
                  <Input
                    placeholder={t("search")}
                    prefix={<SearchOutlined />}
                    onChange={(e) => onSearch(e.target.value)}
                    allowClear
                  />
                  <StyledSelect
                    className="w-100"
                    style={{ minWidth: 140 }}
                    $isFiltered={clickedCompanyFilter !== "All"}
                    onChange={handleShowCompanyFilter}
                    placeholder={t("company")}
                    defaultValue={clickedCompanyFilter !== "null" ? clickedCompanyFilter : "All"}
                  >
                    <Option value="All">{t("all_companies")}</Option>
                    {PRODUCT_COMPANY_GROUPS.map((group) => (
                      <Option key={group.value} value={group.value}>
                        {group.label}
                      </Option>
                    ))}
                  </StyledSelect>
                  <StyledSelect
                    className="w-100"
                    style={{ minWidth: 160 }}
                    $isFiltered={marketingCodeFilter !== "All"}
                    onChange={handleMarketingCodeFilter}
                    defaultValue={marketingCodeFilter !== "null" ? marketingCodeFilter : "All"}
                  >
                    <Option value="All">{t("all_customer_types")}</Option>
                    {MARKETING_CODES.map((code) => (
                      <Option key={code.value} value={code.value}>
                        {code.label}
                      </Option>
                    ))}
                    <Option value="None">{t("no_customer_type")}</Option>
                  </StyledSelect>
                  <StyledSelect
                    className="w-100"
                    style={{ minWidth: 140 }}
                    $isFiltered={countryFilter !== "All"}
                    onChange={handleCountryFilter}
                    placeholder={t("country")}
                    defaultValue={countryFilter !== "null" ? countryFilter : "All"}
                  >
                    <Option value="All">{t("all_countries")}</Option>
                    {countries.map((country) => (
                      <Option key={country.countryCode} value={country.countryCode}>
                        {country.countryCode}
                      </Option>
                    ))}
                  </StyledSelect>
                </Space>
              ) : (
                <Popover
                  content={
                    <FilterPopoverContent>
                      <div>
                        <FilterLabel>{t("search")}</FilterLabel>
                        <Input
                          placeholder={t("search")}
                          prefix={<SearchOutlined />}
                          onChange={(e) => onSearch(e.target.value)}
                          allowClear
                          size="large"
                        />
                      </div>
                      <div>
                        <FilterLabel>{t("company")}</FilterLabel>
                        <Select
                          placeholder={t("company")}
                          style={{ width: "100%" }}
                          value={clickedCompanyFilter !== "null" ? clickedCompanyFilter : "All"}
                          onChange={handleShowCompanyFilter}
                          size="large"
                        >
                          <Option value="All">{t("all_companies")}</Option>
                          {PRODUCT_COMPANY_GROUPS.map((group) => (
                            <Option key={group.value} value={group.value}>
                              {group.label}
                            </Option>
                          ))}
                        </Select>
                      </div>
                      <div>
                        <FilterLabel>{t("customer_type")}</FilterLabel>
                        <Select
                          placeholder={t("customer_type")}
                          style={{ width: "100%" }}
                          value={marketingCodeFilter !== "null" ? marketingCodeFilter : "All"}
                          onChange={handleMarketingCodeFilter}
                          size="large"
                        >
                          <Option value="All">{t("all_customer_types")}</Option>
                          {MARKETING_CODES.map((code) => (
                            <Option key={code.value} value={code.value}>
                              {code.label}
                            </Option>
                          ))}
                          <Option value="None">{t("no_customer_type")}</Option>
                        </Select>
                      </div>
                      <div>
                        <FilterLabel>{t("country")}</FilterLabel>
                        <Select
                          placeholder={t("country")}
                          style={{ width: "100%" }}
                          value={countryFilter !== "null" ? countryFilter : "All"}
                          onChange={handleCountryFilter}
                          size="large"
                        >
                          <Option value="All">{t("all_countries")}</Option>
                          {countries.map((country) => (
                            <Option key={country.countryCode} value={country.countryCode}>
                              {country.countryCode}
                            </Option>
                          ))}
                        </Select>
                      </div>
                      <FilterButtonsContainer>
                        <Button
                          onClick={() => {
                            setSearchValue("");
                            setClickedCompanyFilter("All");
                            setMarketingCodeFilter("All");
                            setCountryFilter("All");
                            localStorage.removeItem("companyFilterValue");
                            localStorage.removeItem("marketingCodeFilterValue");
                            localStorage.removeItem("countryFilterValue");
                            applyFilters("", "All", "All", "All");
                            setFilterPopoverVisible(false);
                          }}
                          style={{ flex: 1 }}
                        >
                          {t("reset")}
                        </Button>
                        <Button type="primary" onClick={() => setFilterPopoverVisible(false)} style={{ flex: 1 }}>
                          {t("apply")}
                        </Button>
                      </FilterButtonsContainer>
                    </FilterPopoverContent>
                  }
                  title={t("filters")}
                  trigger="click"
                  open={filterPopoverVisible}
                  onOpenChange={setFilterPopoverVisible}
                  placement="bottomLeft"
                >
                  <Button
                    icon={<FilterOutlined />}
                    style={{
                      borderColor:
                        searchValue ||
                        clickedCompanyFilter !== "All" ||
                        marketingCodeFilter !== "All" ||
                        countryFilter !== "All"
                          ? "#FF4B33"
                          : "",
                      color:
                        searchValue ||
                        clickedCompanyFilter !== "All" ||
                        marketingCodeFilter !== "All" ||
                        countryFilter !== "All"
                          ? "#FF4B33"
                          : "",
                    }}
                  />
                </Popover>
              )}
              <div className={"d-flex"} style={{ gap: 12 }}>
                <Dropdown overlay={exportMenu}>
                  <Button className="ant-btn-primary">
                    <FileExcelOutlined /> {t("export")} <DownOutlined />
                  </Button>
                </Dropdown>
                <Button type="primary" onClick={onPressAddButton}>
                  {t("add_customer")}
                </Button>
              </div>
            </Space>
            <div className="table-responsive">
              <Table
                showSorterTooltip={false}
                columns={tableColumns}
                dataSource={filteredCompanies}
                rowKey="customerId"
                scroll={{ x: 1300 }}
                loading={companiesLoading || cmsUsersLoading || webshopUsersLoading}
              />
            </div>
          </Card>
        </Col>
      </Row>
      {!!company && (
        <Drawer
          title={t("company_details_value", { company: !!company ? company.customerName : "" })}
          placement="right"
          onClose={closeDrawer}
          open={drawerVisible}
          width={isMobile ? window.innerWidth : 850}
          zIndex={1000}
        >
          <Descriptions
            bordered
            title={
              <div className="flex-row">
                {t("customer_details")}{" "}
                <Tag color={getCustomerStatusColor(company.status)}>{getUserStatus(company.status)}</Tag>
              </div>
            }
            size="small"
            column={1}
          >
            {/* COMPANY DETAILS */}
            <Descriptions.Item label={t("company_group")}>
              {company.company
                ? PRODUCT_COMPANY_GROUPS.find((group) => group.value === company.company)?.label || company.company
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("customer_id")}>
              {profile?.userRole === roles.superAdmin ? (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    const win = window.open("", "_blank");
                    win.document.write(`<pre>${JSON.stringify(company, null, 2)}</pre>`);
                    win.document.close();
                  }}
                  style={{ color: "rgba(0, 0, 0, 0.88)", cursor: "default" }}
                >
                  {company.customerId}
                </a>
              ) : (
                company.customerId
              )}
            </Descriptions.Item>
            {company.company === GROWY && (
              <Descriptions.Item label={t("customer.type")}>
                {!!company.marketingCode ? company.marketingCode : "-"}
              </Descriptions.Item>
            )}
            <Descriptions.Item label={t("exact_account_code")}>
              {!!company.exactAccountCode ? company.exactAccountCode : "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("exact_account_id")}>
              {!!company.exactId ? company.exactId : "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("user.registration_date")}>
              {!!company.createdAt
                ? company.createdAt > 10000000000
                  ? dayjs(company.createdAt).format("DD-MM-YYYY")
                  : dayjs.unix(company.createdAt).format("DD-MM-YYYY")
                : t("registered_before")}
            </Descriptions.Item>
            {company?.companyName && <Descriptions.Item label={t("company")}>{company.companyName}</Descriptions.Item>}
            <Descriptions.Item
              label={company.company === CHEFS_FARM || !company.company ? t("restaurant_name") : t("customer_name")}
            >
              {company.customerName}
            </Descriptions.Item>
            <Descriptions.Item label={t("email")}>
              {!!company.customerEmail ? company.customerEmail : "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("phone")}>
              {!!company.customerPhone ? company.customerPhone : "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("kvk_number")}>
              {!!company.kvkNumber ? company.kvkNumber : "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("tax_number")}>
              {!!company.taxNumber ? company.taxNumber : "-"}
            </Descriptions.Item>
            {!!companyUsers && companyUsers.length > 0 && (
              <Descriptions.Item label={t("contact_person")}>
                {(() => {
                  const userData = companyUsers[0];
                  if (userData)
                    return <span style={{ color: userData.isActive ? "" : "#CF1322" }}>{userData.user}</span>;
                  else return "-";
                })()}
              </Descriptions.Item>
            )}
            <Descriptions.Item label={t("internal_notes")}>
              {!!company.internalNotes ? (
                <Popover
                  content={
                    <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                      <li>
                        <strong>{t("by")}:</strong> {company.internalNotes.updatedBy || company.internalNotes.createdBy}
                      </li>
                      <li>
                        <strong>{t("date")}:</strong>{" "}
                        {dayjs
                          .unix(company.internalNotes.updatedAt || company.internalNotes.createdAt)
                          .format("DD-MM-YYYY, HH:mm")}
                      </li>
                    </ul>
                  }
                >
                  {company.internalNotes.text}
                </Popover>
              ) : (
                "-"
              )}
            </Descriptions.Item>
          </Descriptions>
          <ButtonsWrapper>
            <Button onClick={() => setEditCompanyDetailsModalVisible(true)}>{t("edit_company_details")}</Button>
          </ButtonsWrapper>

          {/* PAYMENT DETAILS */}
          <div className="table-responsive pt-5">
            <Descriptions bordered title={t("payment_details")} size="small" column={1}>
              <Descriptions.Item label={t("payment_method")}>
                {!!company.paymentMethod ? t(company.paymentMethod.toLowerCase().replace(" ", "_")) : "-"}
              </Descriptions.Item>
              <Descriptions.Item label={t("payment_card_number")}>
                {!!company.paymentCardNumber ? company.paymentCardNumber : "-"}
              </Descriptions.Item>
              <Descriptions.Item label={t("payment_card_holder")}>
                {!!company.paymentCardHolder ? company.paymentCardHolder : "-"}
              </Descriptions.Item>
              <Descriptions.Item label={t("customer_discount")}>
                {!!company.customerSnelstartDiscount ? `${company.customerSnelstartDiscount}%` : "-"}
              </Descriptions.Item>
              <Descriptions.Item label={t("credit_limit")}>
                {CURRENCY}
                {!!company.creditLimit ? formatPrice(company.creditLimit) : "-"}
              </Descriptions.Item>
              <Descriptions.Item label={t("customer.invoice_address")}>
                {company.customerBillingAddress}
              </Descriptions.Item>
              {!!company.invoiceCountry && (
                <Descriptions.Item label={t("customer.invoice_country")}>{company.invoiceCountry}</Descriptions.Item>
              )}
              {!!company.invoiceEmail && company.invoiceEmail !== company.customerEmail && (
                <Descriptions.Item label={t("customer.invoice_email")}>{company.invoiceEmail}</Descriptions.Item>
              )}
              {!!company.priceListId && (
                <Descriptions.Item label={t("price_list")}>
                  {getPriceListById(company.priceListId)?.name || company.priceListId}
                </Descriptions.Item>
              )}
            </Descriptions>
            <ButtonsWrapper>
              <Button onClick={() => setEditPaymentDetailsModalVisible(true)}>{t("edit_payment_details")}</Button>
            </ButtonsWrapper>
          </div>

          {/* DELIVERY DETAILS */}
          <div className="table-responsive pt-5">
            <Descriptions
              bordered
              title={<div className="flex-row">{t("delivery_details")}</div>}
              size="small"
              column={1}
            >
              <Descriptions.Item label={t("customer.delivery_address")}>{company.customerAddress}</Descriptions.Item>
              <Descriptions.Item label={t("customer.delivery_country")}>{company.country}</Descriptions.Item>
              <Descriptions.Item label={t("delivery_zone")}>
                {company.deliveryZone?.zone ? <span>{company.deliveryZone.zone}</span> : "-"}
              </Descriptions.Item>
              <Descriptions.Item label={t("delivery_time")}>
                {company.deliveryZone?.deliveryTime || "-"}
              </Descriptions.Item>
              <Descriptions.Item label={t("delivery_days")}>
                {company.deliveryZone?.deliveryDays ? company.deliveryZone.deliveryDays.join(", ") : "-"}
              </Descriptions.Item>

              <Descriptions.Item label={t("address_verified")}>
                {company.addressIsVerified ? t("yes") : t("no")}
              </Descriptions.Item>
              <Descriptions.Item label={t("delivery_days")}>
                {!!company.customerDeliveryDays ? company.customerDeliveryDays : "-"}
              </Descriptions.Item>
              <Descriptions.Item label={t("delivery_cost")}>
                {CURRENCY}
                {company.deliveryCost || company.deliveryCost === 0
                  ? formatPrice(company.deliveryCost)
                  : formatPrice(defaultDeliverySettings?.defaultDeliveryCost || DEFAULT_DELIVERY_COST)}
              </Descriptions.Item>
              <Descriptions.Item label={t("delivery_cost_threshold")}>
                {CURRENCY}
                {company.deliveryCostThreshold || company.deliveryCostThreshold === 0
                  ? formatPrice(company.deliveryCostThreshold)
                  : formatPrice(defaultDeliverySettings?.deliveryCostThreshold || FREE_DELIVERY_FROM_PRICE)}
              </Descriptions.Item>
              <Descriptions.Item label={t("delivery_notes")}>{company.deliveryNotes}</Descriptions.Item>
              <Descriptions.Item label={t("delivery_label")}>{company.deliveryLabel}</Descriptions.Item>
              <Descriptions.Item label={t("delivery_time_slot")}>
                {company.deliveryTimeSlot
                  ? `${company.deliveryTimeSlot.start} - ${company.deliveryTimeSlot.end}${
                      company.deliveryTimeSlot.deliverySurcharge > 0
                        ? ` (+${CURRENCY}${company.deliveryTimeSlot.deliverySurcharge})`
                        : ""
                    }`
                  : "-"}
              </Descriptions.Item>
            </Descriptions>
          </div>
          <ButtonsWrapper>
            <Button onClick={() => setEditDeliveryDetailsModalVisible(true)}>{t("edit_delivery_details")}</Button>
          </ButtonsWrapper>

          {/* USERS */}
          <div className="pt-5">
            <Descriptions bordered title={t("users")} size="small" column={1}>
              {!!companyUsers &&
                companyUsers.length > 0 &&
                companyUsers.map((userData, index) => (
                  <Descriptions.Item key={userData.id} label={index + 1}>
                    <span style={{ opacity: userData.isActive ? 1 : 0.25 }}>{userData.user}</span>
                  </Descriptions.Item>
                ))}
            </Descriptions>
          </div>

          {/* COMMUNICATION PREFERENCES */}
          <div className="pt-5">
            <Descriptions bordered title={t("communication_preferences")} size="small" column={1}>
              {COMMUNICATION_TYPES.map((type) => (
                <Descriptions.Item key={type.value} label={t(type.label)}>
                  {displayCommunicationPreferences(type)}
                </Descriptions.Item>
              ))}
            </Descriptions>
          </div>
          <ButtonsWrapper>
            <Button onClick={() => setEditCommunicationDetailsModalVisible(true)}>
              {t("edit_communication_preferences")}
            </Button>
          </ButtonsWrapper>
        </Drawer>
      )}
      <AddCompanyModal
        visible={addCompanyModalVisible}
        onClose={() => setAddCompanyModalVisible(false)}
        exactCustomers={exactCustomers}
      />
      <QuickEditModal
        company={company}
        visible={quickEditModalVisible}
        onClose={closeQuickEditModal}
        exactCustomers={exactCustomers}
      />
      <EditCompanyDetailsModal
        visible={editCompanyDetailsModalVisible}
        company={company}
        onClose={() => setEditCompanyDetailsModalVisible(false)}
        exactCustomers={exactCustomers}
        webshopUsers={webshopUsers}
        cmsUsers={cmsUsers}
      />
      <EditPaymentDetailsModal
        visible={editPaymentDetailsModalVisible}
        company={company}
        priceLists={priceLists}
        onClose={() => setEditPaymentDetailsModalVisible(false)}
      />
      <EditDeliveryDetailsModal
        visible={editDeliveryDetailsModalVisible}
        company={company}
        onClose={() => setEditDeliveryDetailsModalVisible(false)}
      />
      <EditCommunicationDetailsModal
        visible={editCommunicationDetailsModalVisible}
        company={company}
        users={companyUsers?.filter((user) => user.isActive)}
        onClose={() => setEditCommunicationDetailsModalVisible(false)}
      />
    </>
  );
};

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
`;

const FilterPopoverContent = styled.div`
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FilterLabel = styled.label`
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
`;

const FilterButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  padding-top: 4px;
  border-top: 1px solid #f0f0f0;
  margin-top: 4px;
`;

export default CompanyList;
